<template>
  <Grid
    class="submissions-grid"
    :pageable="pageable"
    :data-items="submissionJobs"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :sort="sort"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @sortchange="sortChangeHandler"
    data-cy="submissions-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e: any) => props.onSortchange(e)"
        @filterchange="(e: any) => props.onFilterchange(e)"
        @closemenu="(e: any) => props.onClosemenu(e)"
        @contentfocus="(e: any) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:DetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <input
          class="fa fa-external-link details-icon"
          type="image"
          alt="Detail Icon"
          @click.prevent="openDetails(dataItem)"
          :src="detailIconUrl"
        />
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridColumnReorderEvent,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
} from "@progress/kendo-vue-grid";
import { ref, watchEffect } from "vue";
import detailIconUrl from "@/assets/img/DetailIcon.png";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import {
  getFilterOperatorsForDate,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { SubmissionsWithSelection } from "@/api/submissionsOverview";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const props = defineProps<{
  submissionJobs: SubmissionsTable[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<SubmissionsTable[]>;
}>();

const emit = defineEmits<{
  (e: "detailsClicked", transaction: SubmissionsWithSelection): void;
  (
    e: "submissionsChanged",
    skip: number,
    take: number,
    filterEvent: GridFilterChangeEvent | null
  ): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const columns = ref<GridColumnPropsExtended[]>([
  {
    cell: "DetailsCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
  },
  {
    title: "Job ID",
    field: "JobId",
    columnMenu: "columnMenu",
  },
  {
    title: "Job Type",
    field: "JobType",
    columnMenu: "columnMenu",
  },
  {
    title: "User Login",
    field: "UserLogin",
    columnMenu: "columnMenu",
  },
  {
    title: "Date Submitted",
    field: "DateProcessedForSort",
    columnMenu: "columnMenu",
    type: "date",
    format: "{0:MM/dd/yyyy h:mm a}",
    allowedFilterOperators: getFilterOperatorsForDate(),
  },
  {
    title: "Email",
    field: "UserEmail",
    columnMenu: "columnMenu",
  },
  {
    title: "Line Items",
    field: "LineItems",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForNumber(),
  },
  {
    title: "Status",
    field: "Status",
    columnMenu: "columnMenu",
  },
]);

let pageSizes = [10, 20, 25, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("submissionsChanged", 0, take.value, e);
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("submissionsChanged", event.page.skip, event.page.take, null);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) columns.value = columnsProps;
  console.debug("SubmissionsGrid.vue, onColumnSubmit", columnsProps);
  emit("columnssubmit", columnsProps);
};

const openDetails = function (transaction: SubmissionsWithSelection) {
  emit("detailsClicked", transaction);
};


const onColumnReorder = function (e: GridColumnReorderEvent) {
  emit("columnreorder", e);
};

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.submissions-grid {
  height: 600px;
}

.k-grid {
  a.is-download-link {
    svg {
      height: 12px;
    }

    &:before {
      content: "";
    }
  }

  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
}
</style>
